import React, { useCallback, useEffect, useState } from "react";
import { Users, Search, Loader2, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Users & Roles admin panel — proper UI replacement for the browser-
 * console `POST /api/admin/users/set-role` workflow.
 *
 * Contract with backend:
 *   • `GET  /api/admin/users/search?q=&limit=` — read-guarded, returns
 *     up to 20 users matching email/username/name substring. Enriched
 *     with `posts_count` and `last_login` (nullable).
 *   • `POST /api/admin/users/set-role`         — write-guarded. Refuses
 *     to demote the last admin (400) — surface the `detail` message
 *     verbatim in a toast so ops know why.
 *
 * Self-lockout guard: dropdown for the current viewer's own row is
 * always disabled, even if backend would accept the change.
 */

const ROLE_BADGE = {
  admin:    { label: "admin",    cls: "bg-tb-sage/25 text-tb-sage border-tb-sage/50" },
  reviewer: { label: "reviewer", cls: "bg-tb-gold/25 text-tb-gold-bright border-tb-gold/50" },
  user:     { label: "user",     cls: "bg-tb-cream-3 text-tb-navy/70 border-tb-navy/15" },
};

function fmt(dt) {
  if (!dt) return "—";
  try {
    const d = new Date(dt);
    return d.toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return String(dt); }
}

export default function UsersRolesPanel() {
  const { user: me } = useAuth();
  const [open, setOpen] = useState(true);
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pendingId, setPendingId] = useState(null);

  const load = useCallback(async (query = "") => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/admin/users/search?q=${encodeURIComponent(query)}&limit=20`,
      );
      setRows(data?.rows || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) load(""); /* initial */ }, [open, load]);

  // Debounced search — 300ms after last keystroke.
  useEffect(() => {
    if (!open) return;
    const h = setTimeout(() => load(q), 300);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, open]);

  const changeRole = async (row, newRole) => {
    if (newRole === row.role) return;
    setPendingId(row.id);
    try {
      const { data } = await api.post("/admin/users/set-role", {
        email: row.email,
        role: newRole,
      });
      toast.success(`Role updated: ${data.email} is now ${data.new_role}`);
      // Optimistic in-place update; then refetch for consistency
      // (posts_count / last_login unchanged, so this is safe).
      setRows((xs) => xs.map((x) => (x.id === row.id ? { ...x, role: data.new_role } : x)));
      load(q);
    } catch (e) {
      // Surface the backend's `detail` verbatim so last-admin refusal
      // reads correctly: "Refusing to demote the last admin — …"
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't update role");
    } finally {
      setPendingId(null);
    }
  };

  return (
    <section
      data-testid="admin-users-roles-panel"
      className="rounded-2xl bg-tb-cream border-2 border-tb-cream-3 p-4 sm:p-5"
    >
      <button
        type="button"
        data-testid="admin-users-roles-toggle"
        data-reviewer-safe="true"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 text-left"
      >
        <span className="w-9 h-9 rounded-full flex items-center justify-center bg-tb-navy/10">
          <Users className="w-4 h-4 text-tb-navy" />
        </span>
        <span className="flex-1">
          <div className="font-serif-it text-tb-navy text-xl">Users &amp; Roles</div>
          <div className="font-ui text-tb-navy/70 text-xs">
            Promote / demote admin, reviewer, or user roles. Search across the roster.
          </div>
        </span>
        {open
          ? <ChevronDown className="w-5 h-5 text-tb-navy/60" />
          : <ChevronRight className="w-5 h-5 text-tb-navy/60" />}
      </button>

      {open && (
        <div className="mt-3 space-y-3">
          <div className="relative" data-reviewer-safe="true">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-tb-navy/50" />
            <input
              data-testid="admin-users-search"
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by email, username, or name"
              className="w-full pl-9 pr-3 py-2 rounded-lg bg-tb-cream-2 border-2 border-tb-cream-3 focus:border-tb-gold outline-none font-ui text-sm"
            />
          </div>

          <div className="overflow-x-auto rounded-xl border border-tb-cream-3">
            <table className="w-full text-sm">
              <thead className="bg-tb-cream-2 text-tb-navy/70 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2">Email</th>
                  <th className="text-left px-3 py-2 hidden md:table-cell">Username</th>
                  <th className="text-left px-3 py-2">Role</th>
                  <th className="text-right px-3 py-2 hidden sm:table-cell">Posts</th>
                  <th className="text-left px-3 py-2 hidden lg:table-cell">Last login</th>
                  <th className="text-right px-3 py-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading && rows.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-6 font-hand text-tb-navy/55 italic">
                    <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />
                    loading users…
                  </td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-6 font-hand text-tb-navy/55 italic">
                    No users match {q ? `"${q}"` : "the roster"}.
                  </td></tr>
                ) : rows.map((r) => {
                  const isSelf = r.id === me?.id;
                  const badge = ROLE_BADGE[r.role] || ROLE_BADGE.user;
                  return (
                    <tr
                      key={r.id}
                      data-testid={`admin-users-row-${r.id}`}
                      className="border-t border-tb-cream-3"
                    >
                      <td className="px-3 py-2 font-ui text-tb-navy truncate max-w-[240px]" title={r.email}>
                        {r.email}
                        {isSelf && <span className="ml-2 text-tb-navy/50 italic text-xs">(you)</span>}
                      </td>
                      <td className="px-3 py-2 font-ui text-tb-navy/80 hidden md:table-cell">{r.username || "—"}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-block px-2 py-0.5 rounded-full border font-hand text-xs ${badge.cls}`}>
                          {badge.label}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right font-ui text-tb-navy tabular-nums hidden sm:table-cell">
                        {r.posts_count ?? 0}
                      </td>
                      <td className="px-3 py-2 font-ui text-tb-navy/70 text-xs hidden lg:table-cell">
                        {r.last_login
                          ? <span title={`${r.last_login.client_ip || ""} · ${r.last_login.created_via || ""}`}>
                              {fmt(r.last_login.at)}
                            </span>
                          : "—"}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <select
                          data-testid={`admin-users-role-select-${r.id}`}
                          data-reviewer-safe={isSelf ? undefined : "true"}
                          value={r.role || "user"}
                          onChange={(e) => changeRole(r, e.target.value)}
                          disabled={isSelf || pendingId === r.id}
                          title={isSelf ? "You can't change your own role" : "Change role"}
                          className={
                            "px-2 py-1 rounded-md bg-tb-cream-2 border-2 border-tb-cream-3 " +
                            "focus:border-tb-gold outline-none font-hand text-sm text-tb-navy " +
                            "disabled:opacity-50 disabled:cursor-not-allowed"
                          }
                        >
                          <option value="admin">admin</option>
                          <option value="reviewer">reviewer</option>
                          <option value="user">user</option>
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <p className="font-hand text-tb-navy/55 italic text-xs">
            Last-admin demotion is refused server-side — the platform always keeps at least one admin.
          </p>
        </div>
      )}
    </section>
  );
}
